import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useLoading } from 'hooks/useLoading';
import { Container } from './styles';
import IEvent from 'interfaces/IEvent';
import EventService from 'services/EventService';
import EventCategoryService from 'services/EventCategoryService';
import Heading from 'components/Heading';
import eyeIcon from 'assets/images/eyeGrayIcon.svg';
import editIcon from 'assets/images/editIcon.svg';
import trashIcon from 'assets/images/trashIcon.svg';
import CategoryTable from 'components/CategoryTable';
import ConfirmationModal from 'components/ConfirmationModal';
import Button from 'components/Button';
import { DataGrid, GridColDef, GridToolbar, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { Grid } from '@mui/material';

const Event = (): JSX.Element => {
  const { openLoading, closeLoading } = useLoading();
  const [events, setEvents] = useState<IEvent[]>([]);
  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const [eventId, setEventId] = useState(0);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    id: false,
  });

  const history = useHistory();
  const service = new EventService();

  useEffect(() => {
    openLoading();
    fetchEvents().finally(() => closeLoading());
  }, []);

  const fetchEvents = async () => {
    try {
      const data = await service.findAll();
      setEvents(data);
    } catch (error) {
      console.error('Erro ao buscar eventos:', error);
    }
  };

  const deleteEvent = async () => {
    openLoading();
    try {
      await service.remove(eventId);
      setEvents(prev => prev.filter(item => item.id !== eventId));
    } finally {
      closeLoading();
    }
  };

  const formatEventDate = (dateString: string): JSX.Element => {
    if (!dateString) return <span>- - -</span>;

    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('pt-BR');

    return (
      <div className="createdDate">
        <span>{formattedDate}</span>
      </div>
    );
  };

  const formatEventCreate = (dateString: string): JSX.Element => {
    if (!dateString) return <span>- - -</span>;

    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('pt-BR');
    const formattedTime = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    return (
      <div className="createdDate">
        <span>{formattedDate}</span>
        <span>{formattedTime}</span>
      </div>
    );
  };

  const eventColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 0.5,
      minWidth: 70,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'createdAt',
      headerName: 'Criação',
      flex: 2,
      minWidth: 250,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => formatEventCreate(params.value as string),
    },
    {
      field: 'updatedAt',
      headerName: 'Última Edição',
      flex: 2,
      minWidth: 250,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => formatEventCreate(params.value as string),
    },
    {
      field: 'user',
      headerName: 'Cadastrado/Editado Por',
      flex: 2,
      minWidth: 250,
      align: 'center',
      headerAlign: 'center',
      valueGetter: params => params.row.user?.name || params.row.user?.email || '- - -',
    },
    {
      field: 'title',
      headerName: 'Título do Evento',
      flex: 2,
      minWidth: 250,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'place',
      headerName: 'Local do Evento',
      flex: 2,
      minWidth: 250,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'price',
      headerName: 'Valor do Evento',
      flex: 2,
      minWidth: 250,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'startDate',
      headerName: 'Data de Início',
      flex: 1,
      minWidth: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => formatEventDate(params.value as string),
    },
    {
      field: 'startTime',
      headerName: 'Hora do Evento',
      flex: 1,
      minWidth: 180,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Ações',
      flex: 1,
      minWidth: 200,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="button-icon"
            title="Visualizar"
            onClick={() => history.push(`/admin/event/${params.id}/preview`)}>
            <img src={eyeIcon} alt="preview" />
          </button>

          <button className="button-icon" title="Editar" onClick={() => history.push(`/event/${params.id}`)}>
            <img src={editIcon} alt="editar" />
          </button>

          <button
            className="button-icon"
            title="Excluir"
            onClick={() => {
              setEventId(Number(params.id));
              setConfirmDeletion(true);
            }}>
            <img src={trashIcon} alt="excluir" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <Container>
      <CategoryTable title="Categorias de Eventos" service={new EventCategoryService()} />
      <div style={{ marginTop: '2rem' }}>
        <div
          className="header"
          style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', alignItems: 'center' }}>
          <Heading>Lista de Eventos Cadastrados pela SETUR</Heading>
          <Button type="submit" buttonType="primary" onClick={() => history.push('/event')}>
            Adicionar
          </Button>
        </div>
        <Grid sx={{ width: '100%', marginBottom: '50px' }}>
          <DataGrid
            sx={{
              background: '#fff',
              boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
              borderRadius: '18px',
              width: '100%',
              border: 'none',
            }}
            rows={events}
            columns={eventColumns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                densitySelector: false,
                exportButton: false,
              },
            }}
            disableRowSelectionOnClick
            autoHeight
          />
        </Grid>
      </div>
      <ConfirmationModal
        open={confirmDeletion}
        title="EXCLUSÃO DE ITEM"
        message="Você tem certeza de que deseja excluir esse item?"
        onCancel={() => setConfirmDeletion(false)}
        onConfirm={() => {
          setConfirmDeletion(false);
          deleteEvent();
        }}
      />
    </Container>
  );
};

export default Event;
